import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Empty, Form, Input, Modal, Select, Skeleton, Space, Tag, Typography } from 'antd';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Icon } from './icons.tsx';
import { useAuth } from './auth.tsx';
import { notifyActionFailure } from './action-feedback.ts';
import {
  ApiRequestError, archiveContract, exportContractAmounts, fetchContractAttachment, getProject, listContracts, listProjects, stageContractFile,
  voidContract, unlinkContractAttachment, releaseContractAttachmentRetention,
  type ArchiveContractDraft,
} from './project-api.ts';
import type { StageContractUploadResponse } from '@youkuai/contracts';
import { DateInput } from './date-input.tsx';
import { isPureExecutorSession } from './project-workspace-access.ts';

const { Text, Title } = Typography;
const projectStatusLabels = { PREPARING: '准备中', ACTIVE: '进行中', SUSPENDED: '已暂停', ARCHIVED: '已归档' } as const;

function fileSizeLabel(bytes: number): string {
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
}

function issueText(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const messages: Record<string, string> = {
      FORBIDDEN: '当前账号没有该项目的操作权限，或权限已被撤销。',
      VERSION_CONFLICT: '项目资料已变化，请刷新后重试。已通过扫描的文件会保留至过期。',
      MALWARE_DETECTED: '文件未通过安全扫描，请更换文件。',
      MALWARE_SCANNER_UNAVAILABLE: '扫描服务暂不可用。文件尚未保存，请稍后重试。',
      OBJECT_STORAGE_UNAVAILABLE: '私有文件存储暂不可用，请稍后重试。',
      FILE_REJECTED: '文件类型或大小不符合要求：仅支持 PDF、PNG、JPEG，最大 50 MiB。',
      EVIDENCE_REQUIRED: '文件暂存已失效、被占用或与项目不符，请重新上传。',
      IDEMPOTENCY_PAYLOAD_CONFLICT: '同一次操作的内容已变化，请重新发起。',
      STATE_CONFLICT: '该档案已失效、附件已解除关联或项目已关闭，请刷新档案后核对。',
      REFERENCE_NOT_FOUND: '合同档案不存在或不属于当前项目。',
      DOWNLOAD_TICKET_EXPIRED: '下载票据已过期，请重新点击下载。',
      DOWNLOAD_TICKET_INVALID: '下载票据无效，请重新点击下载。',
      EXPORT_TICKET_EXPIRED: '导出票据已过期，请重新发起。',
      EXPORT_SOURCE_CHANGED: '合同金额或状态已发生变化，请重新发起导出。',
    };
    return messages[error.code] ?? error.message;
  }
  return error instanceof Error ? error.message : '操作失败，请稍后重试。';
}

export function contractExportIssueText(error: unknown): string {
  if (error instanceof ApiRequestError && error.code === 'FORBIDDEN') {
    return '当前账号缺少“合同金额”独立导出权，或本项目查看范围已变化。请联系管理员核对导出授权。';
  }
  return issueText(error);
}

export function ProjectsPage() {
  const { state } = useAuth();
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [status, setStatus] = useState<'ALL' | 'ACTIVE' | 'PREPARING' | 'SUSPENDED' | 'ARCHIVED'>('ALL');
  const token = state.kind === 'ready' ? state.token : '';
  const accountId = state.kind === 'ready' ? state.session.userId : null;
  const pureExecutor = state.kind === 'ready' && isPureExecutorSession({
    roles: state.session.roles, capabilities: state.session.capabilities });
  const projects = useInfiniteQuery({
    queryKey: ['my-projects', accountId, activeSearch], initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => listProjects(token, activeSearch, pageParam ?? undefined),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined, enabled: !!token && !!accountId,
  });
  const projectItems = projects.data?.pages.flatMap((page) => page.items) ?? [];
  const visibleItems = projectItems.filter((project) => status === 'ALL' || project.status === status);
  const canCreate = state.kind === 'ready' && state.session.capabilities.includes('project.create');
  const activeCount = projectItems.filter((project) => project.status === 'ACTIVE').length;
  const preparingCount = projectItems.filter((project) => project.status === 'PREPARING').length;

  return (
    <main className="page-shell prototype-projects-page">
      <header className="prototype-page-header"><div><span className="prototype-eyebrow">PROJECTS</span>
        <h1>{pureExecutor ? '我的项目' : '项目'}</h1><p>{pureExecutor ? '从项目进入本人参与的内容与任务。' : '查看和管理所有合作项目，以及它们当前所处的执行状态。'}</p></div>
        {canCreate && <Link className="prototype-button is-primary" to="/projects/new"><Icon name="plus" size={16} strokeWidth={2} /> 新建项目</Link>}</header>
      <section className="prototype-project-strip"><div><span>全部项目</span><strong>{projectItems.length}</strong><small>当前账号可访问</small></div>
        <div><span>进行中</span><strong>{activeCount}</strong><small>正在持续推进</small></div>
        <div><span>准备中</span><strong>{preparingCount}</strong><small>等待服务范围确认</small></div>
        <div className="strip-prompt"><span>✦</span><p><b>{activeCount ? `${activeCount} 个项目正在执行` : '可以从一个空项目开始'}</b>
          <small>{activeCount ? '进入项目查看服务包、内容和交付进度' : '合同、团队和服务包都可以稍后补充'}</small></p></div></section>
      <section className="prototype-list-panel"><div className="prototype-list-toolbar"><div className="prototype-segmented">
        {[['ALL','全部'],['ACTIVE','进行中'],['PREPARING','准备中'],['SUSPENDED','已暂停'],['ARCHIVED','已归档']].map(([value,label]) =>
          <button key={value} className={status === value ? 'is-active' : ''} onClick={() => setStatus(value as typeof status)}>{label}</button>)}</div>
        <Input.Search className="prototype-inline-search" value={search} onChange={(event) => setSearch(event.target.value)}
          onSearch={() => setActiveSearch(search)} allowClear placeholder="搜索项目或编号" /></div>
      {projects.isPending && <Skeleton active paragraph={{ rows: 5 }} />}
      {projects.isError && <Alert type="error" showIcon title={projectItems.length ? '后续项目加载失败' : '无法读取本人项目'} description={issueText(projects.error)} />}
      {!projects.isPending && visibleItems.length === 0 && !projects.isError && <Empty
        description={pureExecutor && projectItems.length === 0
          ? '账号已激活，但尚未分配项目。请联系内部管理员将你加入具体项目并设置职责。'
          : '当前筛选下没有项目'} />}
      {visibleItems.length > 0 && <><div className="prototype-project-list-heading"><span>项目</span><span>客户</span><span>状态</span><span>负责人 / 范围</span><span /></div>
      <div className="prototype-project-list">
        {visibleItems.map((project) => <Link className="prototype-project-row" key={project.projectId}
          to={pureExecutor ? `/projects/${project.projectId}/content` : `/projects/${project.projectId}`}>
          <div className="prototype-project-identity"><i>{project.name.slice(0, 1)}</i><span><strong>{project.name}</strong>
            <small>{project.projectCode} · {project.servicePackageCount} 个服务包</small></span></div>
          <div className="prototype-row-cell"><span className="mobile-label">客户</span><b>{project.businessEntity.name}</b><small>{project.businessEntity.code}</small></div>
          <span className={`workspace-status status-${project.status.toLowerCase()}`}><i />{projectStatusLabels[project.status]}</span>
          <div className="prototype-owner-cell"><i>{project.primaryManager.displayName.slice(0, 1)}</i><span><b>{project.primaryManager.displayName}</b>
            <small>{project.memberCount} 位成员 · {project.contractCount} 份合同</small></span></div><span className="prototype-row-arrow">→</span>
        </Link>)}
      </div></>}
      {projectItems.length > 0 && <div className="pagination-rail" aria-live="polite">
        <span>已显示 {projectItems.length} 个本人可访问项目{projects.hasNextPage ? ' · 还有更多' : ' · 已到末页'}</span>
        {projects.hasNextPage && <Button onClick={() => void projects.fetchNextPage()} loading={projects.isFetchingNextPage}>
          {projects.isFetchNextPageError ? '重试加载' : '继续加载项目'}
        </Button>}
      </div>}</section>
    </main>
  );
}

interface ContractFormValues {
  name: string;
  kind: ArchiveContractDraft['kind'];
  parentContractId?: string;
  amountKind: ArchiveContractDraft['amountKind'];
  amount?: string;
  signedOn?: string;
  validFrom?: string;
  validUntil?: string;
  notes?: string;
  relatedProjectIds?: string[];
}

export function ProjectContractsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { state } = useAuth();
  const token = state.kind === 'ready' ? state.token : '';
  const accountId = state.kind === 'ready' ? state.session.userId : null;
  const client = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const [staged, setStaged] = useState<StageContractUploadResponse[]>([]);
  const uploadKeys = useRef<string[]>([]);
  const archiveAttempt = useRef<{ payload: string; key: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [exportPurpose, setExportPurpose] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [voidTarget, setVoidTarget] = useState<{ id: string; code: string; name: string; attachments: number } | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const voidAttempt = useRef<{ payload: string; key: string } | null>(null);
  const [unlinkTarget, setUnlinkTarget] = useState<{
    contractId: string; contractCode: string; attachmentId: string; filename: string;
  } | null>(null);
  const [unlinkReason, setUnlinkReason] = useState('');
  const unlinkAttempt = useRef<{ payload: string; key: string } | null>(null);
  const [releaseTarget, setReleaseTarget] = useState<{
    contractId: string; contractCode: string; attachmentId: string; filename: string;
  } | null>(null);
  const [releaseReason, setReleaseReason] = useState('');
  const releaseAttempt = useRef<{ payload: string; key: string } | null>(null);
  const [form] = Form.useForm<ContractFormValues>();
  const kind = Form.useWatch('kind', form);
  const amountKind = Form.useWatch('amountKind', form);
  const project = useQuery({ queryKey: ['project', accountId, projectId], queryFn: () => getProject(token, projectId!), enabled: !!token && !!accountId && !!projectId });
  const contracts = useInfiniteQuery({
    queryKey: ['project-contracts', accountId, projectId], initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => listContracts(token, projectId!, pageParam ?? undefined),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!token && !!accountId && !!projectId,
  });
  const relatedProjects = useInfiniteQuery({
    queryKey: ['contract-related-projects', accountId], initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => listProjects(token, '', pageParam ?? undefined),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!token && !!accountId,
  });
  const contractItems = contracts.data?.pages.flatMap((page) => page.items) ?? [];
  const archive = useMutation({ mutationFn: archiveContract });
  const voidMutation = useMutation({ mutationFn: voidContract });
  const unlinkMutation = useMutation({ mutationFn: unlinkContractAttachment });
  const releaseMutation = useMutation({ mutationFn: releaseContractAttachmentRetention });
  const canDownload = state.kind === 'ready' && state.session.roles.some((role) => role === 'ADMIN' || role === 'ADMINISTRATION');
  const canMaintain = state.kind === 'ready' && state.session.roles.some((role) =>
    role === 'ADMIN' || role === 'ADMINISTRATION' || role === 'PROJECT_MANAGER' || role === 'BUSINESS');
  const canReleaseRetention = state.kind === 'ready' && state.session.roles.includes('ADMIN');

  async function confirmVoid() {
    if (!projectId || !project.data || !voidTarget || !voidReason.trim()) return;
    const payload = JSON.stringify({ contractId: voidTarget.id, expectedVersion: project.data.version, reason: voidReason.trim() });
    if (voidAttempt.current?.payload !== payload) voidAttempt.current = { payload, key: `void-${crypto.randomUUID()}` };
    setError(null); setSuccess(null);
    try {
      await voidMutation.mutateAsync({ token, projectId, contractId: voidTarget.id, expectedVersion: project.data.version,
        reason: voidReason.trim(), idempotencyKey: voidAttempt.current.key });
      setSuccess(`${voidTarget.code} 已标记失效；原档与 ${voidTarget.attachments} 份附件保留，可新增更正档案。`);
      setVoidTarget(null); setVoidReason(''); voidAttempt.current = null;
      await Promise.all([
        client.invalidateQueries({ queryKey: ['project', accountId, projectId] }),
        client.invalidateQueries({ queryKey: ['project-contracts', accountId, projectId] }),
      ]);
    } catch (failure) { const text = issueText(failure); setError(text); notifyActionFailure(text); }
  }

  async function confirmUnlink() {
    if (!projectId || !project.data || !unlinkTarget || !unlinkReason.trim()) return;
    const payload = JSON.stringify({ ...unlinkTarget, expectedVersion: project.data.version, reason: unlinkReason.trim() });
    if (unlinkAttempt.current?.payload !== payload) unlinkAttempt.current = { payload, key: `unlink-${crypto.randomUUID()}` };
    setError(null); setSuccess(null);
    try {
      await unlinkMutation.mutateAsync({ token, projectId, contractId: unlinkTarget.contractId,
        attachmentId: unlinkTarget.attachmentId, expectedVersion: project.data.version,
        reason: unlinkReason.trim(), idempotencyKey: unlinkAttempt.current.key });
      setSuccess(`${unlinkTarget.contractCode} 的「${unlinkTarget.filename}」已解除当前关联；历史和正式文件仍保留。`);
      setUnlinkTarget(null); setUnlinkReason(''); unlinkAttempt.current = null;
      await Promise.all([
        client.invalidateQueries({ queryKey: ['project', accountId, projectId] }),
        client.invalidateQueries({ queryKey: ['project-contracts', accountId, projectId] }),
      ]);
    } catch (failure) { const text = issueText(failure); setError(text); notifyActionFailure(text); }
  }

  async function confirmReleaseRetention() {
    if (!projectId || !project.data || !releaseTarget || !releaseReason.trim()) return;
    const payload = JSON.stringify({ ...releaseTarget, expectedVersion: project.data.version, reason: releaseReason.trim() });
    if (releaseAttempt.current?.payload !== payload) {
      releaseAttempt.current = { payload, key: `retention-release-${crypto.randomUUID()}` };
    }
    setError(null); setSuccess(null);
    try {
      const result = await releaseMutation.mutateAsync({ token, projectId, contractId: releaseTarget.contractId,
        attachmentId: releaseTarget.attachmentId, expectedVersion: project.data.version,
        reason: releaseReason.trim(), idempotencyKey: releaseAttempt.current.key });
      setSuccess(`${releaseTarget.contractCode} 的「${releaseTarget.filename}」已释放保全；将至少保留至 ${new Date(result.retainUntil).toLocaleString('zh-CN')}。`);
      setReleaseTarget(null); setReleaseReason(''); releaseAttempt.current = null;
      await Promise.all([
        client.invalidateQueries({ queryKey: ['project', accountId, projectId] }),
        client.invalidateQueries({ queryKey: ['project-contracts', accountId, projectId] }),
      ]);
    } catch (failure) { const text = issueText(failure); setError(text); notifyActionFailure(text); }
  }

  async function downloadFile(contractId: string, attachmentId: string, filename: string) {
    if (!projectId || !canDownload) return;
    setDownloadingId(attachmentId); setError(null);
    try {
      const file = await fetchContractAttachment({ token, projectId, contractId, attachmentId });
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url; link.download = filename; link.style.display = 'none';
      document.body.append(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (failure) { const text = issueText(failure); setError(text); notifyActionFailure(text); }
    finally { setDownloadingId(null); }
  }

  async function exportCurrentPage() {
    if (!projectId || exportPurpose.trim().length < 10 || exporting) return;
    setExporting(true); setError(null); setSuccess(null);
    try {
      const result = await exportContractAmounts({ token, projectId, purpose: exportPurpose.trim(),
        afterCode: null, limit: 100 });
      const url = URL.createObjectURL(result.file);
      const link = document.createElement('a');
      link.href = url; link.download = `合同金额-${project.data?.projectCode ?? '项目'}-${result.ticketId}.csv`;
      link.style.display = 'none'; document.body.append(link); link.click(); link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setExportOpen(false); setExportPurpose('');
      setSuccess(`已受控导出 ${result.rowCount} 条合同；用途、范围和文件摘要已记录。`);
    } catch (failure) { setError(contractExportIssueText(failure)); }
    finally { setExporting(false); }
  }

  async function stageFiles() {
    if (!project.data || !projectId || files.length === 0) return;
    if (files.length > 20 || files.some((file) => file.size > 52_428_800 || file.size < 1)) {
      setError('最多选择 20 份附件，每份必须在 1 字节至 50 MiB 之间。'); return;
    }
    setBusy(true); setError(null); setSuccess(null);
    try {
      const next = [...staged];
      for (const [offset, file] of files.slice(staged.length).entries()) {
        const index = staged.length + offset;
        uploadKeys.current[index] ??= `upload-${crypto.randomUUID()}`;
        const result = await stageContractFile({
          token, projectId, expectedVersion: project.data.version, file,
          idempotencyKey: uploadKeys.current[index]!,
        });
        next.push(result);
        setStaged([...next]);
      }
      setSuccess(`${next.length} 份文件已通过服务端扫描，可继续填写并保存合同资料。`);
    } catch (failure) { const text = issueText(failure); setError(text); notifyActionFailure(text); }
    finally { setBusy(false); }
  }

  async function finish(values: ContractFormValues) {
    if (!project.data || !projectId || staged.length === 0) return;
    setError(null); setSuccess(null);
    const draft: ArchiveContractDraft = {
      expectedVersion: project.data.version, name: values.name.trim(), kind: values.kind,
      parentContractId: values.kind === 'ORIGINAL' ? null : values.parentContractId ?? null,
      counterpartyId: project.data.businessEntity.id,
      amountKind: values.amountKind, amount: values.amountKind === 'OPEN' ? null : values.amount?.trim() ?? null,
      signedOn: values.signedOn || null, validFrom: values.validFrom || null,
      validUntil: values.validUntil || null, notes: values.notes?.trim() || null,
      stagingIds: staged.map((item) => item.stagingId),
      relatedProjectIds: values.relatedProjectIds ?? [],
    };
    try {
      const payload = JSON.stringify(draft);
      if (archiveAttempt.current?.payload !== payload) archiveAttempt.current = { payload, key: `archive-${crypto.randomUUID()}` };
      const result = await archive.mutateAsync({ token, projectId, idempotencyKey: archiveAttempt.current.key, draft });
      setSuccess(`合同 ${result.contractCode} 已保存，${result.attachmentIds.length} 份附件已绑定。`);
      setStaged([]); setFiles([]); uploadKeys.current = []; archiveAttempt.current = null; form.resetFields();
      if (fileInput.current) fileInput.current.value = '';
      await Promise.all([
        client.invalidateQueries({ queryKey: ['project', accountId, projectId] }),
        client.invalidateQueries({ queryKey: ['project-contracts', accountId, projectId] }),
        client.invalidateQueries({ queryKey: ['my-projects'] }),
      ]);
    } catch (failure) { const text = issueText(failure); setError(text); notifyActionFailure(text); }
  }

  if (!projectId) return <main className="page-shell"><Alert type="error" title="项目地址不完整" /></main>;
  return (
    <main className="page-shell dossier-shell">
      <Link className="back-link" to={`/projects/${projectId}/packages`}>← 返回项目服务包</Link>
      {project.isPending && <Skeleton active paragraph={{ rows: 5 }} />}
      {project.isError && <Alert type="error" showIcon title="项目不可访问" description={issueText(project.error)} />}
      {project.data && <>
        <div className="dossier-title"><div><div className="section-kicker">{project.data.projectCode} / 合同档案</div>
          <Title level={1}>{project.data.name}</Title><p>{project.data.businessEntity.name} · 负责人 {project.data.primaryManager.displayName}</p></div>
          <div className="dossier-version"><span>当前版本</span><strong>V{project.data.version}</strong></div></div>
        <div className="dossier-note">项目可先独立创建，合同资料后续关联；同一份合同可关联同一客户下的多个项目，不直接约束服务包执行或人员结算。</div>
        {error && <Alert className="workspace-alert" type="error" showIcon title={error} closable onClose={() => setError(null)} />}
        {success && <Alert className="workspace-alert" type="success" showIcon title={success} closable onClose={() => setSuccess(null)} />}
        <div className="dossier-grid">
          <section className="dossier-list" aria-labelledby="dossier-existing-title">
            <div className="panel-heading"><div><span className="panel-number">01 / 已保存</span><h2 id="dossier-existing-title">合同记录</h2></div><Space><span>{contractItems.length} 份</span>
              <Button onClick={() => setExportOpen(true)}>受控导出金额</Button></Space></div>
            {contracts.isPending && <Skeleton active paragraph={{ rows: 4 }} />}
            {contracts.isError && <Alert type="error" title={contractItems.length ? '后续合同加载失败' : '合同列表读取失败'} description={issueText(contracts.error)} />}
            {!contracts.isPending && contractItems.length === 0 && !contracts.isError && <div className="empty-dossier">此项目尚未上传合同资料。项目仍可先开展执行。</div>}
            {contractItems.map((item) => <article className="contract-entry" key={item.contractId}>
              <div className="entry-top"><span>{item.code}</span><Space><Tag color={item.status === 'VOID' ? 'red' : item.amountKind === 'OPEN' ? 'gold' : 'green'}>
                {item.status === 'VOID' ? '已失效' : item.amountKind === 'OPEN' ? '开放金额' : '固定金额'}</Tag></Space></div>
              <h3>{item.name}</h3><p>{item.kind === 'ORIGINAL' ? '原合同' : '补充 / 更正档案'} · {item.amount === null ? '金额未约定' : `¥ ${item.amount}`}</p>
              {item.status === 'VOID' && <div className="contract-void-note">原档保留 · 失效原因：{item.voidReason} · {item.voidedAt ? new Date(item.voidedAt).toLocaleString('zh-CN') : ''}</div>}
              <div className="attachment-lines">{item.attachmentMetadata.map((attachment) => <div
                className={`attachment-line${attachment.unlinkedAt ? ' attachment-line--unlinked' : ''}`} key={attachment.attachmentId}>
                <span className="attachment-filename">↳ {attachment.filename}</span>
                <Text type="secondary" className="attachment-state">{fileSizeLabel(attachment.byteSize)} · {attachment.unlinkedAt
                  ? attachment.reclaimedAt ? '已按政策销毁，摘要永久保留'
                    : attachment.retentionReleasedAt && attachment.retainUntil
                      ? `已释放，至少保留至 ${new Date(attachment.retainUntil).toLocaleDateString('zh-CN')}`
                      : '已解除关联，永久保全'
                  : item.status === 'VOID' ? '历史附件留存' : '已校验'}</Text>
                {attachment.unlinkedAt && <span className="attachment-reason">{attachment.unlinkReason} · {new Date(attachment.unlinkedAt).toLocaleString('zh-CN')}</span>}
                {attachment.retentionReleasedAt && <span className="attachment-reason">保全释放：{attachment.retentionReleaseReason} · {new Date(attachment.retentionReleasedAt).toLocaleString('zh-CN')}</span>}
                {(canDownload || canMaintain) && item.status === 'ARCHIVED' && !attachment.unlinkedAt &&
                  <span className="attachment-actions">
                    {canDownload && <Button size="small" type="link" loading={downloadingId === attachment.attachmentId}
                      onClick={() => void downloadFile(item.contractId, attachment.attachmentId, attachment.filename)}>鉴权下载</Button>}
                    {canMaintain && <Button size="small" type="link" danger
                      onClick={() => { setUnlinkTarget({ contractId: item.contractId, contractCode: item.code,
                        attachmentId: attachment.attachmentId, filename: attachment.filename });
                        setUnlinkReason(''); unlinkAttempt.current = null; setError(null); }}>解除关联</Button>}
                  </span>}
                {canReleaseRetention && attachment.unlinkedAt && attachment.legalHold && !attachment.reclaimedAt && !attachment.retentionReleasedAt &&
                  <span className="attachment-actions"><Button size="small" type="link" danger onClick={() => {
                    setReleaseTarget({ contractId: item.contractId, contractCode: item.code,
                      attachmentId: attachment.attachmentId, filename: attachment.filename });
                    setReleaseReason(''); releaseAttempt.current = null; setError(null);
                  }}>释放保全</Button></span>}
              </div>)}</div>
              {canMaintain && item.status === 'ARCHIVED' && <Button size="small" danger onClick={() => {
                setVoidTarget({ id: item.contractId, code: item.code, name: item.name, attachments: item.attachmentMetadata.length });
                setVoidReason(''); voidAttempt.current = null; setError(null);
              }}>标记失效</Button>}
            </article>)}
            {contractItems.length > 0 && <div className="pagination-rail" aria-live="polite">
              <span>已显示 {contractItems.length} 份档案{contracts.hasNextPage ? ' · 还有更多' : ' · 已到末页'}</span>
              {contracts.hasNextPage && <Button onClick={() => void contracts.fetchNextPage()} loading={contracts.isFetchingNextPage}>
                {contracts.isFetchNextPageError ? '重试加载' : '继续加载合同'}
              </Button>}
            </div>}
          </section>
          <section className="dossier-form" aria-labelledby="dossier-new-title">
            <div className="panel-heading"><div><span className="panel-number">02 / 新增资料</span><h2 id="dossier-new-title">上传合同</h2></div></div>
            <div className={`upload-zone${files.length ? ' has-files' : ''}`}><label htmlFor="contract-files">选择合同附件</label>
              <input id="contract-files" ref={fileInput} type="file" multiple disabled={busy || archive.isPending}
                accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                onChange={(event) => { setFiles(Array.from(event.target.files ?? [])); setStaged([]); uploadKeys.current = []; archiveAttempt.current = null; setError(null); }} />
              <p>{files.length ? `已选择 ${files.length} 份文件；上传后将自动完成安全扫描。` : 'PDF / PNG / JPEG · 每份不超过 50 MiB · 最多 20 份'}</p>
              <Button type={files.length && staged.length !== files.length ? 'primary' : 'default'} onClick={() => void stageFiles()} loading={busy} disabled={!files.length || staged.length === files.length}>上传并安全扫描</Button>
              {staged.map((item) => <div className="staged-file" key={item.stagingId}><Tag color="success">已扫描</Tag>
                <span>{item.originalFilename}</span><Text type="secondary">{item.sha256.slice(0, 12)}…</Text></div>)}
            </div>
            <Form form={form} layout="vertical" initialValues={{ kind: 'ORIGINAL', amountKind: 'OPEN' }} onFinish={(values) => void finish(values)}>
              <Form.Item name="name" label="合同名称" rules={[{ required: true, whitespace: true, message: '填写合同名称' }]}><Input maxLength={120} placeholder="例如：年度内容服务框架协议" /></Form.Item>
              <div className="form-pair"><Form.Item name="kind" label="档案类型"><Select onChange={() => form.setFieldValue('parentContractId', undefined)} options={[
                { value: 'ORIGINAL', label: '原合同' }, { value: 'SUPPLEMENT', label: '补充协议' },
                { value: 'RENEWAL', label: '续签' }, { value: 'CORRECTION', label: '更正' },
              ]} /></Form.Item><Form.Item name="amountKind" label="金额类型"><Select options={[
                { value: 'OPEN', label: '开放金额' }, { value: 'FIXED', label: '固定金额' },
              ]} /></Form.Item></div>
              {kind && kind !== 'ORIGINAL' && <Form.Item name="parentContractId" label="上级合同" rules={[{ required: true, message: '选择同项目上级合同' }]}>
                <Select showSearch optionFilterProp="label" placeholder="选择同项目上级合同" options={contractItems.filter((item) =>
                  item.status === 'ARCHIVED' || kind === 'CORRECTION').map((item) => ({
                  value: item.contractId, label: `${item.code} · ${item.name}${item.status === 'VOID' ? '（已失效，待更正）' : ''}` }))}
                  popupRender={(menu) => <>{menu}{contracts.hasNextPage && <Button className="parent-load-more" type="link"
                    loading={contracts.isFetchingNextPage} onClick={() => void contracts.fetchNextPage()}>
                    {contracts.isFetchNextPageError ? '重试加载更多上级合同' : '找不到上级合同？加载更多'}
                  </Button>}</>} />
              </Form.Item>}
              {amountKind === 'FIXED' && <Form.Item name="amount" label="合同金额（元）" rules={[{ required: true, pattern: /^(?:0|[1-9]\d{0,15})(?:\.\d{1,2})?$/, message: '填写非负金额，最多两位小数' }]}>
                <Input inputMode="decimal" placeholder="0.00" />
              </Form.Item>}
              <div className="form-pair"><Form.Item name="signedOn" label="签署日期"><DateInput /></Form.Item><Form.Item name="validFrom" label="生效日期"><DateInput /></Form.Item></div>
              <Form.Item name="validUntil" label="截止日期"><DateInput /></Form.Item>
              <Form.Item name="notes" label="备注"><Input.TextArea rows={3} maxLength={2000} placeholder="选填，不承载回款或结算信息" /></Form.Item>
              <Form.Item name="relatedProjectIds" label="同时关联其他项目" extra="只显示当前账号可访问的项目；可留空，后续项目不受影响。">
                <Select mode="multiple" allowClear loading={relatedProjects.isFetching} placeholder="选择同一客户下的其他项目"
                  options={(relatedProjects.data?.pages.flatMap((page) => page.items) ?? []).filter((item) =>
                    item.projectId !== projectId && item.businessEntity.id === project.data.businessEntity.id).map((item) => ({
                    value: item.projectId, label: `${item.projectCode} · ${item.name}` }))} />
              </Form.Item>
              <div className="form-foot"><span>{staged.length ? `已就绪：${staged.length} 份附件，可保存合同资料` : '请先选择文件并完成上传扫描'}</span><Button type="primary" htmlType="submit" loading={archive.isPending} disabled={!staged.length || busy}>保存合同资料</Button></div>
            </Form>
          </section>
        </div>
        <Modal title="受控导出合同金额" open={exportOpen} onCancel={() => setExportOpen(false)}
          onOk={() => void exportCurrentPage()} okText="确认导出" okButtonProps={{ disabled: exportPurpose.trim().length < 10 }}
          confirmLoading={exporting} destroyOnHidden>
          <p>本次最多导出按编号倒序的前 100 条合同（包含已失效档案）；下载仅限本人、五分钟内有效，服务端会重新核验权限和金额。超过 100 条的页面导出尚未开放。</p>
          <label htmlFor="contract-export-purpose">用途（至少 10 字，必填）</label>
          <Input.TextArea id="contract-export-purpose" value={exportPurpose} rows={3} maxLength={500}
            onChange={(event) => setExportPurpose(event.target.value)} placeholder="例如：核对本项目合同金额与商务台账" />
        </Modal>
        <Modal title={`标记合同档案失效 · ${voidTarget?.code ?? ''}`} open={!!voidTarget}
          okText="确认标记失效" okButtonProps={{ danger: true, disabled: !voidReason.trim() || voidReason.trim().length > 500 }}
          confirmLoading={voidMutation.isPending} onOk={() => void confirmVoid()} onCancel={() => { if (!voidMutation.isPending) {
            setVoidTarget(null); setVoidReason(''); voidAttempt.current = null;
          } }}>
          <p>即将标记「{voidTarget?.name}」失效，影响 1 份档案、{voidTarget?.attachments ?? 0} 份附件。原始记录与附件继续保留，不从项目删除；普通下载入口将关闭。若有正确文件，请另建更正档案。</p>
          <label htmlFor="contract-void-reason">失效原因（必填）</label>
          <Input.TextArea id="contract-void-reason" value={voidReason} maxLength={500} showCount rows={3}
            placeholder="例如：上传错误，需以更正档案替代" onChange={(event) => setVoidReason(event.target.value)} />
          {error && <Alert className="workspace-alert" type="error" showIcon title={error} />}
        </Modal>
        <Modal title={`解除附件关联 · ${unlinkTarget?.contractCode ?? ''}`} open={!!unlinkTarget}
          okText="确认解除关联" okButtonProps={{ danger: true, disabled: !unlinkReason.trim() || unlinkReason.trim().length > 500 }}
          confirmLoading={unlinkMutation.isPending} onOk={() => void confirmUnlink()}
          onCancel={() => { if (!unlinkMutation.isPending) { setUnlinkTarget(null); setUnlinkReason(''); unlinkAttempt.current = null; } }}>
          <p>影响 1 份附件：「{unlinkTarget?.filename}」。解除后普通下载关闭，合同档案仍显示这条历史；原始文件默认保全，不会因解除关联立即删除。若需正确文件，请新增更正档案。</p>
          <label htmlFor="contract-unlink-reason">解除原因（必填）</label>
          <Input.TextArea id="contract-unlink-reason" value={unlinkReason} maxLength={500} showCount rows={3}
            placeholder="例如：附件关联错误，另建更正档案" onChange={(event) => setUnlinkReason(event.target.value)} />
          {error && <Alert className="workspace-alert" type="error" showIcon title={error} />}
        </Modal>
        <Modal title={`释放附件保全 · ${releaseTarget?.contractCode ?? ''}`} open={!!releaseTarget}
          okText="确认释放保全" okButtonProps={{ danger: true, disabled: !releaseReason.trim() || releaseReason.trim().length > 500 }}
          confirmLoading={releaseMutation.isPending} onOk={() => void confirmReleaseRetention()}
          onCancel={() => { if (!releaseMutation.isPending) {
            setReleaseTarget(null); setReleaseReason(''); releaseAttempt.current = null;
          } }}>
          <p>影响 1 份附件：「{releaseTarget?.filename}」。此操作仅用于已解除业务关联的错误上传；确认后不会立即删除，服务端将固定继续保留 365 天。到期时仍有引用或法律保全则不会销毁。</p>
          <p>政策 v1.0 · 预计最早保留至 {new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString('zh-CN')}。本弹窗是操作二次确认，不新增审批人。</p>
          <label htmlFor="contract-retention-release-reason">释放原因（必填）</label>
          <Input.TextArea id="contract-retention-release-reason" value={releaseReason} maxLength={500} showCount rows={3}
            placeholder="例如：错误上传的重复文件，业务关联已解除且无其他引用"
            onChange={(event) => setReleaseReason(event.target.value)} />
          {error && <Alert className="workspace-alert" type="error" showIcon title={error} />}
        </Modal>
      </>}
    </main>
  );
}
